import { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { applicationsApi } from './api/applications';
import { customizationsApi } from './api/customizations';
import {
	ApplicationContext,
	IApplicationContext
} from './contexts/ApplicationContext';
import { FbPixelContext } from './contexts/FbPixelContext';
import { Router } from './routes';
import { ToastViewport } from './components/Toast';

export const App = () => {
	const [fbPixel, setFbPixel] = useState<string>();
	const [application, setApplication] = useState<IApplicationContext>({});

	const { data: customizationsData } = useQuery(
		[customizationsApi.find.key],
		customizationsApi.find.fetch,
		{
			refetchOnWindowFocus: false
		}
	);

	const { data: financeData } = useQuery(
		[applicationsApi.getFinanceParams.key],
		applicationsApi.getFinanceParams.fetch,
		{
			refetchOnWindowFocus: false
		}
	);

	useEffect(() => {
		if (!financeData) return;

		const {
			isAddressRequired,
			isAnticipationEnabled,
			installmentRates,
			isAntifraudEnabled,
			isBoletoNPixInstallmentEnabled,
			shouldDisplayTerms,
			isCurseducaPayConfigured,
			gateway,
			paymentsAvailable
		} = financeData;

		setApplication((prev) => ({
			...prev,
			finance: {
				isCurseducaPayConfigured: isCurseducaPayConfigured ?? true,
				isAddressRequired,
				isAnticipationEnabled,
				installmentRates,
				isAntifraudEnabled,
				isBoletoNPixInstallmentEnabled,
				shouldDisplayTerms,
				gateway,
				paymentsAvailable
			}
		}));
	}, [financeData]);

	useEffect(() => {
		if (!customizationsData) return;

		const { customization } = customizationsData;

		if (
			customization.url &&
			!customization.url.startsWith('https://') &&
			!customization.url.startsWith('http://')
		) {
			customization.url = `https://${customization.url}`;
		}

		setApplication(() => ({
			...application,
			customization: {
				...customization,
				primaryColor: `#${customization.primaryColor}`,
				secondaryColor: `#${customization.secondaryColor}`
			}
		}));
	}, [customizationsData]);

	useEffect(() => {
		if (application.name) document.title = application.name;
	}, [application]);

	/**
	 * Tema da PLATAFORMA. Roda em effect (não no corpo do render, que
	 * reaplicava a cada renderização) e cede a vez quando a página do
	 * checkout define um tema próprio — marcado em `data-theme-override`
	 * pelo builder (ver useCheckoutPage).
	 */
	useEffect(() => {
		if (document.body.dataset.themeOverride) return;

		const isDark =
			customizationsData?.customization?.theme?.title === 'DARK' &&
			window?.location?.pathname !== '/credit-cards/create';

		if (isDark) document.body.classList.add('dark-mode');
	}, [customizationsData]);

	return (
		<ApplicationContext.Provider value={application}>
			<FbPixelContext.Provider value={{ fbPixel, setFbPixel }}>
				<Router />
				<ToastViewport />
			</FbPixelContext.Provider>
		</ApplicationContext.Provider>
	);
};
